import React, { useContext, useEffect, useReducer, useState } from 'react';
import api from '../api';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { Store } from '../Store';
import { getError } from '../utils';
import { useTranslation } from 'react-i18next';
import { Table, Card, Row, Col } from 'react-bootstrap';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        font: action.payload.font,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };
    default:
      return state;
  }
};

export default function FontEditScreen() {
  const [{ loading, error, loadingUpdate, font }, dispatch] = useReducer(
    reducer,
    {
      loading: true,
      error: '',
      font: {},
    }
  );

  const { t } = useTranslation();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const params = useParams();
  const { id: fontId } = params; // deconstracts the id out of the params variable and its name is set to fontId

  const [triggerFetchRequest, setTriggerFetchRequest] = useState(false);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await api.get(`/api/fonts/${fontId}`);
        setIsActive(data.isActive);

        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            font: data,
          },
        });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [fontId, userInfo, triggerFetchRequest]);

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      await api.put(`/api/fonts/${fontId}`, {
        isActive,
      });

      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      setTriggerFetchRequest(!triggerFetchRequest);
      toast.success('Font aktualisiert');
    } catch (error) {
      toast.error(t(getError(error)));
      setIsActive(font.isActive);
      dispatch({ type: 'UPDATE_FAIL' });
    }
  };

  return (
    <Container>
      <title>{font.fontFamily}</title>

      <h1 className="screen-heading">{font.fontFamily}</h1>

      <Container>
        {loading || loadingUpdate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox> // Show error message if there's an error
        ) : (
          <>
            <Form onSubmit={submitHandler}>
              <Form.Group className="my-4" controlId="isActive">
                <Form.Select
                  className="mobile-device-compatible"
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value)}
                  required
                >
                  <option className="option-tag" value={true}>
                    Enabled
                  </option>
                  <option className="option-tag" value={false}>
                    Disabled
                  </option>
                </Form.Select>
              </Form.Group>

              <div className="scrollable-container">
                <div className="d-flex">
                  <Card
                    style={{
                      border: '1px solid #f1f1f1',
                      borderRadius: '15px',
                      backgroundColor: '#fbfbfb',
                      width: '400px',
                      height: 'auto',
                      margin: '0 5px',
                      fontSize: '14px',
                    }}
                  >
                    <Card.Body
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Card.Title className="text-center my-3">
                        {font.primaryFont.fontName}
                      </Card.Title>

                      <div className="mx-3">
                        <Row className="mt-5">
                          <Col md={4}>
                            <span
                              style={{
                                textTransform: 'lowercase',
                              }}
                            >
                              Style:
                            </span>
                          </Col>
                          <Col md={8}>
                            <strong className="no-wrap">
                              {font.primaryFont.style}
                            </strong>
                          </Col>
                        </Row>

                        <Row className="mt-3">
                          <Col md={4}>
                            <span
                              style={{
                                textTransform: 'lowercase',
                              }}
                            >
                              Preview:
                            </span>
                          </Col>
                          <Col md={8}>
                            <img
                              src={font.primaryFont.previewUrl}
                              alt={`${font.primaryFont.fontName} Preview`}
                              style={{
                                maxWidth: '100%', // Scale down if image is too wide
                                maxHeight: '100%', // Scale down if image is too tall
                                height: 'auto', // Maintain aspect ratio
                                width: 'auto', // Maintain aspect ratio
                                objectFit: 'contain', // Ensure the image fits within the container while keeping its aspect ratio
                              }}
                            />
                          </Col>
                        </Row>

                        <Row className="mt-3">
                          <Col md={4}>
                            <span
                              style={{
                                textTransform: 'lowercase',
                              }}
                            >
                              TTF URL:
                            </span>
                          </Col>
                          <Col md={8} style={{ fontSize: '12px' }}>
                            <a
                              href={font.primaryFont.ttfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: 'underline',
                              }}
                            >
                              {font.primaryFont.ttfUrl}
                            </a>
                          </Col>
                        </Row>
                      </div>

                      {font.primaryFont.variations.length > 0 ? (
                        <Table
                          striped
                          bordered
                          hover
                          responsive
                          className="fixed-table mt-5"
                          style={{ fontSize: '14px' }}
                        >
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Min</th>
                              <th>Max</th>
                              <th>Default</th>
                            </tr>
                          </thead>
                          <tbody>
                            {font.primaryFont.variations.map(
                              (variation, index) => (
                                <tr key={index}>
                                  <td>{variation.name}</td>
                                  <td>{variation.minValue}</td>
                                  <td>{variation.maxValue}</td>
                                  <td>
                                    {variation.defaultValue !== undefined
                                      ? variation.defaultValue
                                      : 'N/A'}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      ) : null}
                    </Card.Body>
                  </Card>

                  {/* Display Additional Styles */}
                  {font.additionalStyles.map((style) => (
                    <Card
                      style={{
                        border: '1px solid #f1f1f1',
                        borderRadius: '15px',
                        backgroundColor: '#fbfbfb',
                        width: '400px',
                        height: 'auto',
                        margin: '0 5px',
                        fontSize: '14px',
                      }}
                    >
                      <Card.Body
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Card.Title className="text-center my-3">
                          {style.fontName}
                        </Card.Title>

                        <div className="mx-3">
                          <Row className="mt-5">
                            <Col xs={4}>
                              <span
                                style={{
                                  textTransform: 'lowercase',
                                }}
                              >
                                Style:
                              </span>
                            </Col>
                            <Col xs={8}>
                              <strong>{style.style}</strong>
                            </Col>
                          </Row>

                          <Row className="mt-3">
                            <Col xs={4}>
                              <span
                                style={{
                                  textTransform: 'lowercase',
                                }}
                              >
                                Preview:
                              </span>
                            </Col>
                            <Col xs={8}>
                              <img
                                src={style.previewUrl}
                                alt={`${style.fontName} Preview`}
                                style={{
                                  maxWidth: '100%', // Scale down if image is too wide
                                  maxHeight: '100%', // Scale down if image is too tall
                                  height: 'auto', // Maintain aspect ratio
                                  width: 'auto', // Maintain aspect ratio
                                  objectFit: 'contain', // Ensure the image fits within the container while keeping its aspect ratio
                                }}
                              />
                            </Col>
                          </Row>

                          <Row className="mt-3">
                            <Col xs={4}>
                              <span
                                style={{
                                  textTransform: 'lowercase',
                                }}
                              >
                                TTF URL:
                              </span>
                            </Col>
                            <Col md={8} style={{ fontSize: '12px' }}>
                              <a
                                href={style.ttfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  textDecoration: 'underline',
                                }}
                              >
                                {style.ttfUrl}
                              </a>
                            </Col>
                          </Row>
                        </div>

                        {style.variations.length > 0 ? (
                          <Table
                            striped
                            bordered
                            hover
                            responsive
                            className="fixed-table mt-5"
                            style={{ fontSize: '14px' }}
                          >
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Min</th>
                                <th>Max</th>
                                <th>Default</th>
                              </tr>
                            </thead>
                            <tbody>
                              {style.variations.map((variation, index) => (
                                <tr key={index}>
                                  <td>{variation.name}</td>
                                  <td>{variation.minValue}</td>
                                  <td>{variation.maxValue}</td>
                                  <td>
                                    {variation.defaultValue !== undefined
                                      ? variation.defaultValue
                                      : 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : null}
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="mb-3 text-center mt-5">
                <Button
                  variant="secondary" // Button styling variant
                  disabled={loadingUpdate} // Disable button while updating
                  type="submit" // Submit the form
                >
                  Speichern
                </Button>
              </div>
            </Form>
          </>
        )}
      </Container>
    </Container>
  );
}
